/**
 * PageGuide - the plain-language "what is this page / what do I do here" line
 * (comprehension layer, 2026-06-23). A first-time user who has never seen a
 * DSPM gets a one-line explanation in everyday words at the top of each page;
 * an experienced operator dismisses it once (persisted per page in
 * localStorage) and never sees it again - so it teaches newcomers without
 * nagging experts.
 */
import { useState, type ReactNode } from 'react';
import { Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export function PageGuide({
  id,
  children,
  className,
}: {
  /** Stable per-page key so dismissal is remembered for that page only. */
  id: string;
  children: ReactNode;
  className?: string;
}): JSX.Element | null {
  const storageKey = `foctta.guide.dismissed.${id}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  return (
    <div
      className={cn(
        'glass-lite rounded-md border border-info/25 px-3 py-2 mb-4 flex items-start gap-2 text-xs',
        className,
      )}
    >
      <Info className="h-4 w-4 text-info shrink-0 mt-0.5" />
      <p className="flex-1 text-muted-foreground leading-relaxed">
        <span className="text-foreground/90 font-medium">In plain terms: </span>
        {children}
      </p>
      <button
        type="button"
        onClick={() => {
          try {
            localStorage.setItem(storageKey, '1');
          } catch {
            /* localStorage may be unavailable */
          }
          setDismissed(true);
        }}
        aria-label="Got it, hide this"
        title="Got it, hide this"
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
